import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, Star } from 'lucide-react';
import { API } from '../lib/api';

const LABELS = ['', 'Very poor', 'Poor', 'Okay', 'Good', 'Excellent'];

const StarPicker = ({ value, onPick, accent }) => (
  <div className="flex justify-center gap-2" role="radiogroup" aria-label="Rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onPick(n)} aria-label={`${n} star${n > 1 ? 's' : ''}`} data-testid={`feedback-star-${n}`}
        className="rounded-full p-1 transition-transform hover:scale-110 active:scale-95">
        <Star size={44} strokeWidth={1.5} fill={n <= value ? accent : 'none'} color={n <= value ? accent : '#cbd5e1'} />
      </button>
    ))}
  </div>
);

const GoogleBadge = ({ g }) => (g?.rating ? (
  <p className="mt-2 text-xs text-slate-500" data-testid="public-google-rating">
    <span className="font-semibold text-slate-700">{g.rating.toFixed(1)} ★</span> from {g.count} reviews on Google
  </p>
) : null);

export default function PublicFeedback() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [step, setStep] = useState('rate');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const base = `${API}/public/feedback/${token}`;

  useEffect(() => {
    axios.get(base).then(({ data }) => {
      setInfo(data);
      if (data.rating) { setRating(data.rating); setComment(data.comment || ''); setStep(data.rating >= 4 ? 'happy' : 'sorry'); }
    }).catch((e) => setErr(e?.response?.data?.detail || 'This link is not valid.'));
  }, [base]);

  const send = (r, c) => axios.post(base, { rating: r, comment: c || '' });
  const pick = async (n) => {
    setRating(n); setStep(n >= 4 ? 'happy' : 'sorry');
    try { await send(n, comment); } catch (e) { setErr(e?.response?.data?.detail || 'Could not save. Please try again.'); }
  };
  const toGoogle = () => {
    window.open(info.review_url, '_blank', 'noopener');
    if (comment) {
      navigator.clipboard?.writeText(comment).then(() => setCopied(true)).catch(() => {});
      send(rating, comment).catch(() => {});
    }
    axios.post(`${base}/google`).catch(() => {});
  };
  const submitPrivate = async () => {
    setBusy(true);
    try { await send(rating, comment); setStep('thanks'); } catch (e) { setErr(e?.response?.data?.detail || 'Could not send.'); } finally { setBusy(false); }
  };

  if (err && !info) return <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-center text-slate-600" data-testid="feedback-invalid">{err}</div>;
  if (!info) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  const accent = info.brand_color || '#0f766e';
  const textarea = (placeholder) => (
    <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={2000} placeholder={placeholder} data-testid="feedback-comment"
      className="mt-4 w-full rounded-xl border border-slate-300 p-3 text-sm focus:border-slate-500 focus:outline-none" />
  );

  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-50 px-4 py-10 sm:items-center" data-testid="public-feedback-page">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 text-center shadow-sm sm:p-8">
        {info.logo_url ? <img src={info.logo_url} alt={info.workshop} className="mx-auto h-14 object-contain" data-testid="feedback-logo" />
          : <div className="text-lg font-semibold" style={{ color: accent }}>{info.workshop}</div>}
        <GoogleBadge g={info.google} />
        {step !== 'thanks' && (
          <>
            <h1 className="mt-6 text-2xl font-semibold text-slate-900" data-testid="feedback-question">
              {info.first_name ? `${info.first_name}, how` : 'How'} did we do{info.vehicle ? ` with your ${info.vehicle}` : ''}?
            </h1>
            <p className="mt-1 text-sm text-slate-500">Tap a star. It takes 10 seconds.</p>
            <div className="mt-5"><StarPicker value={rating} onPick={pick} accent={accent} /></div>
            {rating > 0 && <p className="mt-2 text-sm font-semibold text-slate-700" data-testid="feedback-rating-label">{LABELS[rating]}</p>}
          </>
        )}
        {step === 'happy' && (
          <div data-testid="feedback-happy">
            <p className="mt-5 text-[15px] text-slate-700">Thank you! Would you share this on Google? It helps other car owners find {info.workshop}.</p>
            {textarea('What did you like? (we will copy it for you to paste on Google)')}
            {info.review_url && (
              <button type="button" onClick={toGoogle} data-testid="feedback-google-btn" style={{ background: accent }}
                className="mt-4 w-full rounded-full px-6 py-3.5 text-[15px] font-semibold text-white">Post this on Google Maps</button>
            )}
            {copied && <p className="mt-2 text-xs text-emerald-700" data-testid="feedback-copied"><Check size={12} className="inline" /> Comment copied. Paste it into the Google review box.</p>}
          </div>
        )}
        {step === 'sorry' && (
          <div data-testid="feedback-sorry">
            <p className="mt-5 text-[15px] text-slate-700">We're sorry it wasn't great. What went wrong? This goes straight to the {info.workshop} team.</p>
            {textarea('Tell us what happened and how we can make it right')}
            <button type="button" disabled={busy} onClick={submitPrivate} data-testid="feedback-private-submit" style={{ background: accent }}
              className="mt-4 w-full rounded-full px-6 py-3.5 text-[15px] font-semibold text-white disabled:opacity-60">Send to the team</button>
            {info.review_url && <a href={info.review_url} target="_blank" rel="noreferrer" onClick={() => axios.post(`${base}/google`).catch(() => {})} className="mt-4 block text-xs text-slate-400 underline" data-testid="feedback-google-link">or post a review on Google</a>}
          </div>
        )}
        {step === 'thanks' && (
          <div className="mt-6" data-testid="feedback-thanks">
            <h1 className="text-2xl font-semibold text-slate-900">Thank you for telling us.</h1>
            <p className="mt-2 text-sm text-slate-600">The {info.workshop} team will get in touch{info.phone ? `, or call us on ${info.phone}` : ''}.</p>
            {info.review_url && <a href={info.review_url} target="_blank" rel="noreferrer" className="mt-4 block text-xs text-slate-400 underline">or post a review on Google</a>}
          </div>
        )}
        {err && <p className="mt-3 text-xs text-rose-600" data-testid="feedback-error">{err}</p>}
      </div>
    </div>
  );
}
